import React, { createContext, useContext, ReactNode } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

export type Language = "en" | "de" | "es";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  getLocalizedPath: (path: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const translations: Record<Language, Record<string, string>> = {
  en: {
    // Navigation
    "nav.home": "Home",
    "nav.products": "Products",
    "nav.industries": "Industries",
    "nav.contacts": "Contacts",
    "nav.getOffer": "Get Offer",
    
    // Hero
    "hero.title": "Industrial Components & Spare Parts for Critical Industries",
    "hero.subtitle": "Reliable supply. Professional support. Fast response.",
    "hero.cta.offer": "Get Offer",
    "hero.cta.products": "Browse Products",
    
    // About
    "about.title": "About KM KIT",
    "about.text1": "KM KIT is a supplier of industrial components, spare parts and complete solutions for production facilities.",
    "about.text2": "We support engineers, purchasing departments and technical directors with reliable equipment sourcing and technical assistance.",
    
    // Industries Section
    "industries.title": "Industries We Serve",
    "industries.subtitle": "We deliver specialized components and support to critical industrial sectors",
    "industries.viewAll": "View All Industries",
    "industries.pageTitle": "Specialized solutions and expert support for critical industrial sectors",
    "industries.pageSubtitle": "KMKIT supplies industrial components, spare parts and automation solutions tailored to production sites and factories. Click an industry to view detailed product types, common tasks, our solutions and trusted suppliers.",
    "industries.viewDetails": "View Details",
    "industries.backTo": "Back to Industries",
    
    // Industry Detail
    "industry.typicalProducts": "Typical products",
    "industry.typicalTasks": "Typical tasks",
    "industry.ourSolutions": "Our solutions",
    "industry.keySuppliers": "Key suppliers we work with",
    "industry.ourAdvantages": "Our advantages",
    "industry.requestQuote": "Request Quote",
    "industry.openCatalog": "Open Catalog",
    "industry.contactUs": "Contact Us",
    "industry.productsFor": "Products for",
    "industry.productsSubtitle": "Browse our catalog of components specifically for this industry",
    "industry.fallbackMessage": "No products found for this specific industry. Showing products from the general catalog instead.",
    
    // Products
    "products.title": "Products Catalog",
    "products.subtitle": "Our catalog is automatically synced. Use filters to find the required components.",
    "products.searchPlaceholder": "Search products...",
    "products.allManufacturers": "All Manufacturers",
    "products.allTypes": "All Types",
    "products.showing": "Showing",
    "products.of": "of",
    "products.products": "products",
    "products.loading": "Loading products...",
    "products.error": "Could not load products",
    "products.loadingError": "Loading Error",
    "products.errorMessage": "Sorry, an error occurred while loading products.",
    "products.tryAgain": "Try again",
    "products.unavailable": "Unavailable",
    "products.noProducts": "No products found?!",
    "products.noProductsHint": "Try adjusting your filters or search query.",
    "products.viewDetails": "View Details",

    // Product Detail
    "product.related": "Related Products",
    "product.description": "Detailed Description",
    "product.specifications": "Specifications",
    "product.category": "Category",
    "product.productId": "Product ID",
    "product.deliveryTime": "Delivery Time",
    "product.guarantees": "Guarantees",
    "product.guarantee1": "100% original products",
    "product.guarantee2": "Certificates of conformity",
    "product.guarantee3": "Warranty service",
    "product.guarantee4": "Technical documentation",
    "product.oneTimeRequest": "One-time Request",
    "product.contractRequest": "Contract Request",
    "product.weAlsoDo": "We also provide:",
    "product.linkCopied": "Link copied to clipboard",
    "product.shareError": "Failed to share link",
    "product.price": "Price",
    "product.delivery": "Delivery",
    "product.availability": "Availability",
    "product.check": "Inquire",
    "product.onRequest": "On request",
    "product.deliveryDays": "20-25 business days",
    "product.backToCatalog": "Back to Catalog",
    "product.catalog": "Catalog",
    "product.comingSoon": "Coming soon",

    // Product Not Found
    "productNotFound.title": "Product not found",
    "productNotFound.description": "Sorry, we could not find the product you are looking for.",
    "productNotFound.backToCatalog": "Back to Catalog",
    
    // Product Range
    "productRange.title": "Our Product Range",
    "productRange.subtitle": "We supply a wide range of industrial components from leading global manufacturers. The full catalog is dynamically updated to ensure the most current product information.",
    "productRange.cta": "Open Catalog",
    
    "products.noProductsFor": "No products found for",
    // Why Choose
    "whyChoose.title": "Why Clients Choose KM KIT",
    "whyChoose.wideRange.title": "Wide Product Range",
    "whyChoose.wideRange.desc": "Thousands of industrial components from trusted brands.",
    "whyChoose.fastResponse.title": "Fast Response & Delivery",
    "whyChoose.fastResponse.desc": "Efficient sourcing and logistics for critical industries.",
    "whyChoose.expertise.title": "Technical Expertise",
    "whyChoose.expertise.desc": "Support for engineers and maintenance teams.",
    "whyChoose.reliable.title": "Reliable Partner",
    "whyChoose.reliable.desc": "Transparent processes, consistent communication, long-term cooperation.",
    
    // CTA
    "cta.title": "Need a quotation or support?",
    "cta.subtitle": "Our team is ready to assist you with your industrial component needs",
    "cta.button": "Request a Quote",
    
    // Contacts
    "contacts.title": "Request an Offer or Contact Us",
    "contacts.subtitle": "Our team will respond within 24 hours",
    "contacts.form.title": "Send Your Request",
    "contacts.form.name": "Full Name *",
    "contacts.form.company": "Company *",
    "contacts.form.email": "Email *",
    "contacts.form.phone": "Phone",
    "contacts.form.product": "Product of Interest",
    "contacts.form.manufacturer": "Manufacturer",
    "contacts.form.message": "Message *",
    "contacts.form.submit": "Send Request",
    "contacts.form.success.title": "Request Sent Successfully",
    "contacts.form.success.description": "Our team will respond within 24 hours.",
    "contacts.info.title": "Contact Information",
    "contacts.info.email": "Email",
    "contacts.info.phone": "Phone",
    "contacts.info.hours": "Working Hours",
    "contacts.info.hours.days": "Monday - Friday",
    "contacts.info.hours.time": "9:00 AM - 6:00 PM",
    "contacts.info.location": "Location",
    "contacts.guarantee.title": "Fast Response Guarantee",
    "contacts.guarantee.description": "We understand the importance of quick turnaround in industrial operations. Our team commits to responding to all inquiries within 24 hours.",

    // Footer
    "footer.quickLinks": "Quick Links",
    "footer.industries": "Industries",
    "footer.contact": "Contact",
    "footer.copyright": "All rights reserved.",
  },
  de: {
    // Navigation
    "nav.home": "Startseite",
    "nav.products": "Produkte",
    "nav.industries": "Branchen",
    "nav.contacts": "Kontakt",
    "nav.getOffer": "Angebot anfordern",
    
    // Hero
    "hero.title": "Industriekomponenten & Ersatzteile für kritische Industrien",
    "hero.subtitle": "Zuverlässige Lieferung. Professionelle Unterstützung. Schnelle Reaktion.",
    "hero.cta.offer": "Angebot anfordern",
    "hero.cta.products": "Produkte durchsuchen",
    
    // About
    "about.title": "Über KM KIT",
    "about.text1": "KM KIT ist ein Lieferant von Industriekomponenten, Ersatzteilen und kompletten Lösungen für Produktionsanlagen.",
    "about.text2": "Wir unterstützen Ingenieure, Einkaufsabteilungen und technische Direktoren mit zuverlässiger Ausrüstungsbeschaffung und technischer Unterstützung.",
    
    // Industries Section
    "industries.title": "Branchen, die wir bedienen",
    "industries.subtitle": "Wir liefern spezialisierte Komponenten und Unterstützung für kritische Industriesektoren",
    "industries.viewAll": "Alle Branchen anzeigen",
    "industries.pageTitle": "Spezialisierte Lösungen und Expertenunterstützung für kritische Industriesektoren",
    "industries.pageSubtitle": "KMKIT liefert Industriekomponenten, Ersatzteile und Automatisierungslösungen, die auf Produktionsstätten und Fabriken zugeschnitten sind. Klicken Sie auf eine Branche, um detaillierte Produkttypen, häufige Aufgaben, unsere Lösungen und vertrauenswürdige Lieferanten anzuzeigen.",
    "industries.viewDetails": "Details anzeigen",
    "industries.backTo": "Zurück zu Branchen",
    
    // Industry Detail
    "industry.typicalProducts": "Typische Produkte",
    "industry.typicalTasks": "Typische Aufgaben",
    "industry.ourSolutions": "Unsere Lösungen",
    "industry.keySuppliers": "Wichtige Lieferanten, mit denen wir zusammenarbeiten",
    "industry.ourAdvantages": "Unsere Vorteile",
    "industry.requestQuote": "Angebot anfordern",
    "industry.openCatalog": "Katalog öffnen",
    "industry.contactUs": "Kontaktieren Sie uns",
    "industry.productsFor": "Produkte für",
    "industry.productsSubtitle": "Durchsuchen Sie unseren Katalog mit Komponenten speziell für diese Branche",
    "industry.fallbackMessage": "Für diese spezielle Branche wurden keine Produkte gefunden. Stattdessen werden Produkte aus dem allgemeinen Katalog angezeigt.",
    
    // Products
    "products.title": "Produktkatalog",
    "products.subtitle": "Unser Katalog wird automatisch synchronisiert. Verwenden Sie Filter, um die erforderlichen Komponenten zu finden.",
    "products.searchPlaceholder": "Produkte suchen...",
    "products.allManufacturers": "Alle Hersteller",
    "products.allTypes": "Alle Typen",
    "products.showing": "Zeige",
    "products.of": "von",
    "products.products": "Produkten",
    "products.loading": "Produkte werden geladen...",
    "products.error": "Produkte konnten nicht geladen werden",
    "products.loadingError": "Ladefehler",
    "products.errorMessage": "Beim Laden der Produkte ist ein Fehler aufgetreten.",
    "products.tryAgain": "Erneut versuchen",
    "products.unavailable": "Nicht verfügbar",
    "products.noProducts": "Keine Produkte gefunden?!",
    "products.noProductsHint": "Versuchen Sie, Ihre Filter oder Suchanfrage anzupassen.",
    "products.viewDetails": "Details anzeigen",

    // Product Detail
    "product.related": "Ähnliche Produkte",
    "product.description": "Detaillierte Beschreibung",
    "product.specifications": "Spezifikationen",
    "product.category": "Kategorie",
    "product.productId": "Produkt-ID",
    "product.deliveryTime": "Lieferzeit",
    "product.guarantees": "Garantien",
    "product.guarantee1": "100% Originalprodukte",
    "product.guarantee2": "Konformitätszertifikate",
    "product.guarantee3": "Garantieservice",
    "product.guarantee4": "Technische Dokumentation",
    "product.oneTimeRequest": "Einmalige Anfrage",
    "product.contractRequest": "Vertragsanfrage",
    "product.weAlsoDo": "Wir bieten auch:",
    "product.linkCopied": "Link in die Zwischenablage kopiert",
    "product.shareError": "Link konnte nicht geteilt werden",
    "product.price": "Preis",
    "product.delivery": "Lieferung",
    "product.availability": "Verfügbarkeit",
    "product.check": "Anfragen",
    "product.onRequest": "Auf Anfrage",
    "product.deliveryDays": "20-25 Werktage",
    "product.backToCatalog": "Zurück zum Katalog",
    "product.catalog": "Katalog",
    "product.comingSoon": "Demnächst",

    // Product Not Found
    "productNotFound.title": "Produkt nicht gefunden",
    "productNotFound.description": "Entschuldigung, wir konnten das gesuchte Produkt nicht finden.",
    "productNotFound.backToCatalog": "Zurück zum Katalog",

    // Product Range
    "productRange.title": "Unser Produktsortiment",
    "productRange.subtitle": "Wir liefern ein breites Sortiment an Industriekomponenten von führenden globalen Herstellern. Der vollständige Katalog wird dynamisch aktualisiert, um die aktuellsten Produktinformationen zu gewährleisten.",
    "productRange.cta": "Katalog öffnen",
    
    "products.noProductsFor": "Keine Produkte gefunden für",
    // Why Choose
    "whyChoose.title": "Warum Kunden KM KIT wählen",
    "whyChoose.wideRange.title": "Breites Produktsortiment",
    "whyChoose.wideRange.desc": "Tausende von Industriekomponenten von vertrauenswürdigen Marken.",
    "whyChoose.fastResponse.title": "Schnelle Reaktion & Lieferung",
    "whyChoose.fastResponse.desc": "Effiziente Beschaffung und Logistik für kritische Industrien.",
    "whyChoose.expertise.title": "Technische Expertise",
    "whyChoose.expertise.desc": "Unterstützung für Ingenieure und Wartungsteams.",
    "whyChoose.reliable.title": "Zuverlässiger Partner",
    "whyChoose.reliable.desc": "Transparente Prozesse, konsistente Kommunikation, langfristige Zusammenarbeit.",
    
    // CTA
    "cta.title": "Benötigen Sie ein Angebot oder Unterstützung?",
    "cta.subtitle": "Unser Team ist bereit, Ihnen bei Ihren Industriekomponenten-Bedürfnissen zu helfen",
    "cta.button": "Angebot anfordern",
    
    // Contacts
    "contacts.title": "Angebot anfordern oder uns kontaktieren",
    "contacts.subtitle": "Unser Team antwortet innerhalb von 24 Stunden",
    "contacts.form.title": "Senden Sie Ihre Anfrage",
    "contacts.form.name": "Vollständiger Name *",
    "contacts.form.company": "Firma *",
    "contacts.form.email": "E-Mail *",
    "contacts.form.phone": "Telefon",
    "contacts.form.product": "Interessierendes Produkt",
    "contacts.form.manufacturer": "Hersteller",
    "contacts.form.message": "Nachricht *",
    "contacts.form.submit": "Anfrage senden",
    "contacts.form.success.title": "Anfrage erfolgreich gesendet",
    "contacts.form.success.description": "Unser Team wird innerhalb von 24 Stunden antworten.",
    "contacts.info.title": "Kontaktinformationen",
    "contacts.info.email": "E-Mail",
    "contacts.info.phone": "Telefon",
    "contacts.info.hours": "Arbeitszeiten",
    "contacts.info.hours.days": "Montag - Freitag",
    "contacts.info.hours.time": "9:00 - 18:00 Uhr",
    "contacts.info.location": "Standort",
    "contacts.guarantee.title": "Garantie für schnelle Antwort",
    "contacts.guarantee.description": "Wir verstehen die Bedeutung einer schnellen Abwicklung im Industriebetrieb. Unser Team verpflichtet sich, alle Anfragen innerhalb von 24 Stunden zu beantworten.",

    // Footer
    "footer.quickLinks": "Schnelllinks",
    "footer.industries": "Branchen",
    "footer.contact": "Kontakt",
    "footer.copyright": "Alle Rechte vorbehalten.",
  },
  es: {
    // Navigation
    "nav.home": "Inicio",
    "nav.products": "Productos",
    "nav.industries": "Industrias",
    "nav.contacts": "Contacto",
    "nav.getOffer": "Solicitar Oferta",
    
    // Hero
    "hero.title": "Componentes Industriales y Repuestos para Industrias Críticas",
    "hero.subtitle": "Suministro confiable. Soporte profesional. Respuesta rápida.",
    "hero.cta.offer": "Solicitar Oferta",
    "hero.cta.products": "Ver Productos",
    
    // About
    "about.title": "Sobre KM KIT",
    "about.text1": "KM KIT es un proveedor de componentes industriales, repuestos y soluciones completas para instalaciones de producción.",
    "about.text2": "Apoyamos a ingenieros, departamentos de compras y directores técnicos con suministro confiable de equipos y asistencia técnica.",
    
    // Industries Section
    "industries.title": "Industrias que Atendemos",
    "industries.subtitle": "Entregamos componentes especializados y soporte a sectores industriales críticos",
    "industries.viewAll": "Ver Todas las Industrias",
    "industries.pageTitle": "Soluciones especializadas y soporte experto para sectores industriales críticos",
    "industries.pageSubtitle": "KMKIT suministra componentes industriales, repuestos y soluciones de automatización adaptadas a sitios de producción y fábricas. Haga clic en una industria para ver tipos de productos detallados, tareas comunes, nuestras soluciones y proveedores de confianza.",
    "industries.viewDetails": "Ver Detalles",
    "industries.backTo": "Volver a Industrias",
    
    // Industry Detail
    "industry.typicalProducts": "Productos típicos",
    "industry.typicalTasks": "Tareas típicas",
    "industry.ourSolutions": "Nuestras soluciones",
    "industry.keySuppliers": "Proveedores clave con los que trabajamos",
    "industry.ourAdvantages": "Nuestras ventajas",
    "industry.requestQuote": "Solicitar Cotización",
    "industry.openCatalog": "Abrir Catálogo",
    "industry.contactUs": "Contáctenos",
    "industry.productsFor": "Productos para",
    "industry.productsSubtitle": "Explore nuestro catálogo de componentes específicamente para esta industria",
    "industry.fallbackMessage": "No se encontraron productos para esta industria específica. En su lugar, se muestran productos del catálogo general.",
    
    // Products
    "products.title": "Catálogo de Productos",
    "products.subtitle": "Nuestro catálogo se sincroniza automáticamente. Use filtros para encontrar los componentes requeridos.",
    "products.searchPlaceholder": "Buscar productos...",
    "products.allManufacturers": "Todos los Fabricantes",
    "products.allTypes": "Todos los Tipos",
    "products.showing": "Mostrando",
    "products.of": "de",
    "products.products": "productos",
    "products.loading": "Cargando productos...",
    "products.error": "No se pudieron cargar los productos",
    "products.loadingError": "Error de Carga",
    "products.errorMessage": "Lo sentimos, ocurrió un error al cargar los productos.",
    "products.tryAgain": "Intentar de nuevo",
    "products.unavailable": "No disponible",
    "products.noProducts": "No se encontraron productos?!",
    "products.noProductsHint": "Intente ajustar sus filtros o consulta de búsqueda.",
    "products.viewDetails": "Ver Detalles",

    // Product Detail
    "product.related": "Productos Relacionados",
    "product.description": "Descripción Detallada",
    "product.specifications": "Especificaciones",
    "product.category": "Categoría",
    "product.productId": "ID del Producto",
    "product.deliveryTime": "Tiempo de Entrega",
    "product.guarantees": "Garantías",
    "product.guarantee1": "Productos 100% originales",
    "product.guarantee2": "Certificados de conformidad",
    "product.guarantee3": "Servicio de garantía",
    "product.guarantee4": "Documentación técnica",
    "product.oneTimeRequest": "Solicitud Única",
    "product.contractRequest": "Solicitud de Contrato",
    "product.weAlsoDo": "También ofrecemos:",
    "product.linkCopied": "Enlace copiado al portapapeles",
    "product.shareError": "Error al compartir el enlace",
    "product.price": "Precio",
    "product.delivery": "Entrega",
    "product.availability": "Disponibilidad",
    "product.check": "Consultar",
    "product.onRequest": "Bajo petición",
    "product.deliveryDays": "20-25 días hábiles",
    "product.backToCatalog": "Volver al Catálogo",
    "product.catalog": "Catálogo",
    "product.comingSoon": "Próximamente",

    // Product Not Found
    "productNotFound.title": "Producto no encontrado",
    "productNotFound.description": "Lo sentimos, no pudimos encontrar el producto que está buscando.",
    "productNotFound.backToCatalog": "Volver al Catálogo",

    // Product Range
    "productRange.title": "Nuestra Gama de Productos",
    "productRange.subtitle": "Suministramos una amplia gama de componentes industriales de fabricantes globales líderes. El catálogo completo se actualiza dinámicamente para garantizar la información de productos más actual.",
    "productRange.cta": "Abrir Catálogo",
    
    "products.noProductsFor": "No se encontraron productos para",
    // Why Choose
    "whyChoose.title": "Por Qué los Clientes Eligen KM KIT",
    "whyChoose.wideRange.title": "Amplia Gama de Productos",
    "whyChoose.wideRange.desc": "Miles de componentes industriales de marcas confiables.",
    "whyChoose.fastResponse.title": "Respuesta y Entrega Rápida",
    "whyChoose.fastResponse.desc": "Abastecimiento y logística eficientes para industrias críticas.",
    "whyChoose.expertise.title": "Experiencia Técnica",
    "whyChoose.expertise.desc": "Soporte para ingenieros y equipos de mantenimiento.",
    "whyChoose.reliable.title": "Socio Confiable",
    "whyChoose.reliable.desc": "Procesos transparentes, comunicación consistente, cooperación a largo plazo.",
    
    // CTA
    "cta.title": "¿Necesita una cotización o soporte?",
    "cta.subtitle": "Nuestro equipo está listo para asistirle con sus necesidades de componentes industriales",
    "cta.button": "Solicitar Cotización",
    
    // Contacts
    "contacts.title": "Solicitar una Oferta o Contáctenos",
    "contacts.subtitle": "Nuestro equipo responderá en 24 horas",
    "contacts.form.title": "Envíe su Solicitud",
    "contacts.form.name": "Nombre Completo *",
    "contacts.form.company": "Empresa *",
    "contacts.form.email": "Correo Electrónico *",
    "contacts.form.phone": "Teléfono",
    "contacts.form.product": "Producto de Interés",
    "contacts.form.manufacturer": "Fabricante",
    "contacts.form.message": "Mensaje *",
    "contacts.form.submit": "Enviar Solicitud",
    "contacts.form.success.title": "Solicitud Enviada con Éxito",
    "contacts.form.success.description": "Nuestro equipo responderá en 24 horas.",
    "contacts.info.title": "Información de Contacto",
    "contacts.info.email": "Correo Electrónico",
    "contacts.info.phone": "Teléfono",
    "contacts.info.hours": "Horario de Atención",
    "contacts.info.hours.days": "Lunes - Viernes",
    "contacts.info.hours.time": "9:00 AM - 6:00 PM",
    "contacts.info.location": "Ubicación",
    "contacts.guarantee.title": "Garantía de Respuesta Rápida",
    "contacts.guarantee.description": "Entendemos la importancia de una rápida respuesta en las operaciones industriales. Nuestro equipo se compromete a responder a todas las consultas en 24 horas.",

    // Footer
    "footer.quickLinks": "Enlaces Rápidos",
    "footer.industries": "Industrias",
    "footer.contact": "Contacto",
    "footer.copyright": "Todos los derechos reservados.",
  },
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { lang } = useParams<{ lang?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const language: Language = (lang as Language) || "en";

  const t = (key: string): string => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const getLocalizedPath = (path: string): string => {
    // Remove leading slash if present
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    return `/${language}/${cleanPath}`;
  };

  const setLanguage = (newLang: Language) => {
    const currentPath = location.pathname;
    // Remove current language prefix and add new one
    const pathWithoutLang = currentPath.replace(/^\/(en|de|es)/, "");
    const newPath = `/${newLang}${pathWithoutLang || ""}`;
    navigate(newPath);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, getLocalizedPath }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

